import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CaregiverProfile {
  name: string;
  avatar_url: string | null;
  role: string;
  last_shift_date: string | null;
}

export interface CaregiverKPIs {
  total_tasks: number;
  on_time_pct: number;
  late_count: number;
  incidents: number;
  shifts_count: number;
  checkin_on_time_pct: number | null;
  completion_pct: number | null;
}

export interface DailyActivity {
  date: string;
  on_time: number;
  late: number;
}

export interface DailyPunctuality {
  date: string;
  task_pct: number | null;
  checkin_pct: number | null;
}

export interface PatientServed {
  patient_id: string;
  nickname: string;
  photo_url: string | null;
  tasks: number;
  incidents: number;
  shifts_count: number;
  task_on_time_pct: number | null;
  checkin_on_time_pct: number | null;
  completion_pct: number | null;
}

export interface DailyShiftDetail {
  shift_id: string;
  patient_id: string;
  patient_nickname: string;
  check_in_at: string;
  check_out_at: string | null;
  expected_start: string | null;
  late_minutes: number | null;
  is_on_time: boolean | null;
  status: string;
}

export interface CaregiverDetailData {
  profile: CaregiverProfile;
  kpis: CaregiverKPIs;
  daily_activity: DailyActivity[];
  daily_punctuality: DailyPunctuality[];
  daily_shifts: Record<string, DailyShiftDetail[]>;
  patients_served: PatientServed[];
}

export function useCaregiverDetail(companyId: string | undefined, caregiverId: string | undefined, periodDays: number, patientId?: string | null) {
  return useQuery({
    queryKey: ['caregiver-detail', companyId, caregiverId, periodDays, patientId],
    queryFn: async (): Promise<CaregiverDetailData> => {
      const body: Record<string, any> = {
        company_id: companyId,
        caregiver_user_id: caregiverId,
        period_days: periodDays,
      };
      if (patientId) body.patient_id = patientId;

      const { data, error } = await supabase.functions.invoke('generate-caregiver-detail', { body });

      if (error) throw error;
      return data as CaregiverDetailData;
    },
    enabled: !!companyId && !!caregiverId,
    staleTime: 5 * 60 * 1000,
  });
}
